import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { getCookie } from 'cookies-next';
import {
  randomPassword,
  removeTrailingSlash,
  validatePhoneNumber,
} from '@requity-homes/utils';
import {
  Link,
  Redirect,
  Text,
  trackEvent,
  Heading,
  sendLoginLink,
  Spinner,
} from '@requity-homes/component-library';
import { Auth, Hub, I18n } from 'aws-amplify';
import {
  Authenticator,
  CheckboxField,
  PhoneNumberField,
  TextField,
  useAuthenticator,
} from '@aws-amplify/ui-react';
import { COOKIE_UTM_KEY, REFERRAL_CODE } from '../../const';
import Router from 'next/router';

interface UtmDataInterface {
  cookieInitialRef?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  signupOrigin?: string;
}

let referralCode: string = null;
let utmData: Partial<UtmDataInterface> = {};

let countryCode = null;

let signUpUserEmail = null;

I18n.putVocabulariesForLanguage('en', {
  'Create Account': 'Sign Up',
});

const authComponents = {
  Header() {
    return (
      <div className="md:-mt-72">
        <Head>
          <title>Client Sign-Up | Requity Homes</title>
          <meta
            name="description"
            content="Start your journey towards home ownership with Requity Homes. Register today to explore rent-to-own properties and customize your home buying plan."
          />
        </Head>
        <Heading
          level="h1"
          element="h1"
          fontFace="sans"
          className="px-8 md:text-5xl text-3xl font-bold text-center"
        >
          Create Account
        </Heading>
        <Text className="px-8 pt-4 text-center">
          Sign up as a client so you can track your rent-to-own application from
          start to finish.
        </Text>
      </div>
    );
  },
  SignUp: {
    Header() {
      return <></>;
    },
    FormFields() {
      if (window) {
        referralCode = getCookie(REFERRAL_CODE);

        const stringifiedUTMData = getCookie(COOKIE_UTM_KEY);
        if (stringifiedUTMData) {
          utmData = JSON.parse(stringifiedUTMData);
        } else {
          // Direct Trafic
          utmData.utmSource = 'portal.requityhomes.com';
        }
      }

      const { validationErrors } = useAuthenticator();

      return (
        <>
          <TextField
            placeholder="First name"
            required
            name={'given_name'}
            label={undefined}
            errorMessage={validationErrors['given_name'] as string}
            hasError={!!validationErrors['given_name']}
          />

          <TextField
            placeholder="Last name"
            required
            name={'family_name'}
            label={undefined}
            errorMessage={validationErrors['family_name'] as string}
            hasError={!!validationErrors['family_name']}
          />

          <TextField
            placeholder="Email"
            required
            name={'email'}
            type={'email'}
            label={undefined}
            errorMessage={validationErrors['email'] as string}
            hasError={!!validationErrors['email']}
          />

          <PhoneNumberField
            defaultCountryCode="+1"
            required
            name={'phone_number'}
            label={undefined}
            placeholder="Phone number"
            errorMessage={validationErrors['phone_number'] as string}
            hasError={!!validationErrors['phone_number']}
            onCountryCodeChange={(event) => {
              countryCode = event.target.value;
            }}
          />

          <Text className="pt-4 text-center text-xs">
            By creating an account, you agree to Requity Homes&apos;{' '}
            <Link href="https://www.requityhomes.com/legal">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href="https://www.requityhomes.com/privacy-policy">
              Privacy Policy
            </Link>
            . No opt-in information will be shared with third parties for
            marketing purposes.
          </Text>

          <CheckboxField
            errorMessage={validationErrors['custom:hasOptedInComm'] as string}
            hasError={!!validationErrors['custom:hasOptedInComm']}
            name={'custom:hasOptedInComm'}
            value="yes"
            label={
              <Text className="text-xs">
                I agree to receive text messages from Requity Homes related to
                my Rent-to-Own application, including updates, reminders, and
                customer support communication. Message frequency may vary.
                Standard message and data rates may apply. You can opt out
                anytime by replying &quot;STOP&quot;
              </Text>
            }
          />

          <CheckboxField
            name={'custom:hasOptedInMarketing'}
            value="yes"
            label={
              <Text className="text-xs">
                I agree to receive promotional text messages from Requity Homes
                about rent-to-own offers, new programs, and special promotions.
                Message frequency may vary. Consent is not a condition of
                applying or participating in the rent-to-own program. You can
                unsubscribe anytime by replying &quot;STOP&quot;
              </Text>
            }
          />
          <input
            type="hidden"
            name="custom:referralCode"
            value={referralCode}
          />
          {utmData.cookieInitialRef && (
            <input
              type="hidden"
              name="custom:cookieInitialRef"
              value={utmData.cookieInitialRef}
            />
          )}
          {utmData.utmSource && (
            <input
              type="hidden"
              name="custom:utmSource"
              value={utmData.utmSource}
            />
          )}
          {utmData.utmMedium && (
            <input
              type="hidden"
              name="custom:utmMedium"
              value={utmData.utmMedium}
            />
          )}
          {utmData.utmCampaign && (
            <input
              type="hidden"
              name="custom:utmCampaign"
              value={utmData.utmCampaign}
            />
          )}
          {utmData.utmContent && (
            <input
              type="hidden"
              name="custom:utmContent"
              value={utmData.utmContent}
            />
          )}
          {utmData.utmTerm && (
            <input
              type="hidden"
              name="custom:utmTerm"
              value={utmData.utmTerm}
            />
          )}
          {utmData.signupOrigin && (
            <input
              type="hidden"
              name="custom:signupOrigin"
              value={utmData.signupOrigin}
            />
          )}
        </>
      );
    },
    Footer() {
      return (
        <>
          <Text className="px-8 pb-8 text-center font-bold text-lg">
            Already have an account?{' '}
            <Link href="/auth/sign-in" className="text-coral-med no-underline">
              Log in
            </Link>
          </Text>
        </>
      );
    },
  },
};

const formFields = {
  signIn: {
    username: {
      label: 'Email',
      placeholder: 'Email',
      isRequired: true,
    },
    password: {
      isRequired: false,
    },
  },
};

const services = {
  async handleSignUp(formData) {
    let result = {};
    signUpUserEmail = formData?.username;
    formData.password = randomPassword();
    if (countryCode) {
      // remove the default +1 code and add the current country code
      formData.attributes.phone_number =
        formData.attributes.phone_number.replace('+1', '');
      formData.attributes.phone_number = `${countryCode}${formData.attributes.phone_number}`;
    }
    try {
      result = await Auth.signUp({
        ...formData,
      });
      trackEvent('signup');
      // automatically sign-in user
      await Auth.signIn(formData.username, formData.password);
      const session = await Auth.currentSession();
      if (session) {
        console.log('Successfully authenticated user');
        return Router.push('/');
      } else {
        console.log('Could not authenticate user. Sending login link');
        await sendLoginLink(
          `${removeTrailingSlash(process.env.NEXT_PUBLIC_AUTH_API_URL)}/login`,
          formData?.username,
        );
      }
    } catch (e) {
      console.debug(e);
    }

    return result;
  },
  async validateCustomSignUp(formData) {
    let hasErrors = false;
    const validationErrors = {};
    // Add validation for given_name, family_name, email
    const {
      given_name: givenName = null,
      family_name: familyName = null,
      email = null,
      phone_number: phoneNumber = null,
      'custom:hasOptedInComm': hasOptedInComm = false,
    } = formData;
    const isGivenNameValid = givenName === null || givenName.length > 0;
    const isFamilyNameValid = familyName === null || familyName.length > 0;
    const isEmailValid = email === null || email.length > 0;
    const isPhoneNumberValid =
      phoneNumber === null || validatePhoneNumber(phoneNumber, countryCode);

    if (!isGivenNameValid) {
      hasErrors = true;
      validationErrors['given_name'] = 'Please provide your first name';
    }

    if (!isFamilyNameValid) {
      hasErrors = true;
      validationErrors['family_name'] = 'Please provide your last name';
    }

    if (!isEmailValid) {
      hasErrors = true;
      validationErrors['email'] = 'Please provide your email';
    }

    if (!hasOptedInComm) {
      hasErrors = true;
      validationErrors['custom:hasOptedInComm'] = ' ';
    }

    if (!isPhoneNumberValid) {
      hasErrors = true;
      validationErrors['phone_number'] = 'Please provide a valid phone number';
    }
    console.log('validationErrors', validationErrors, formData);
    return hasErrors ? Promise.resolve(validationErrors) : undefined;
  },
  async handleConfirmSignUp({ username, code }) {
    try {
      await Auth.confirmSignUp(username, code);
    } catch (e) {
      console.debug('Error confirming sign up', e);
    }

    window.history.pushState({}, document.title, window.location.pathname);
  },
};

export default function SignUpPage() {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // listing for signUp_failure event
    Hub.listen('auth', (data) => {
      if (
        data.payload?.event === 'signUp_failure' &&
        data.payload.data?.code === 'UsernameExistsException'
      ) {
        setLoading(true);
        console.log(
          `User already exists. Sending login link to ${signUpUserEmail}`,
        );
        return sendLoginLink(
          `${removeTrailingSlash(process.env.NEXT_PUBLIC_AUTH_API_URL)}/login`,
          signUpUserEmail,
        );
      }
    });
  }, []);
  return (
    <>
      {loading && <Spinner className="bg-white-pure" />}
      <Authenticator
        formFields={formFields}
        components={authComponents}
        services={services}
        variation={'modal'}
        initialState="signUp"
        hideSignUp={false}
        className={'global-auth-wrapper sign-up'}
      >
        {() => <Redirect url="/" />}
      </Authenticator>
    </>
  );
}
